import { AppConfig } from './app-config'
import { BackendServerOption } from '../all-types'

export const SERVER_OPTIONS_STORAGE_KEY = 'billdesk_backend_options'
export const SELECTED_SERVER_STORAGE_KEY = 'billdesk_selected_backend_url'

export interface ServerOption {
  id: string
  name: string
  url: string
}

const normalizeUrl = (url: string) => url.trim().replace(/\/+$/, '')

const getOptionName = (url: string) => {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

const parseServerOption = (rawOption: string, index: number): ServerOption | null => {
  const trimmedOption = rawOption.trim()

  if (!trimmedOption) {
    return null
  }

  const [rawName, ...rawUrlParts] = trimmedOption.split('|')
  const hasName = rawUrlParts.length > 0
  const url = normalizeUrl(hasName ? rawUrlParts.join('|') : rawName)

  if (!url) {
    return null
  }

  return {
    id: `${index}-${url}`,
    name: hasName && rawName.trim() ? rawName.trim() : getOptionName(url),
    url,
  }
}

const parseRawServerOptions = (rawOptions?: string | null) => {

  if (!rawOptions) {
    return []
  }

  try {
    const parsedOptions = JSON.parse(rawOptions)

    if (Array.isArray(parsedOptions)) {
      return parsedOptions
        .map((option, index) => {
          if (typeof option === 'string') {
            return parseServerOption(option, index)
          }

          if (option && typeof option.url === 'string') {
            return {
              id: `${index}-${option.url}`,
              name: typeof option.name === 'string' && option.name.trim()
                ? option.name.trim()
                : getOptionName(option.url),
              url: normalizeUrl(option.url),
            }
          }

          return null
        })
        .filter((option): option is ServerOption => Boolean(option))
    }
  } catch {
    return rawOptions
      .split(',')
      .map(parseServerOption)
      .filter((option): option is ServerOption => Boolean(option))
  }

  return []
}

const parseConfiguredServerOptions = () => parseRawServerOptions(AppConfig.getApiBaseUrlOptions())

export const saveStoredBackendOptions = (backendOptions: BackendServerOption[]) => {
  localStorage.setItem(SERVER_OPTIONS_STORAGE_KEY, JSON.stringify(backendOptions))
}

const getStoredServerOptions = (): ServerOption[] => {
  try {
    const rawOptions = localStorage.getItem(SERVER_OPTIONS_STORAGE_KEY)
    const parsedOptions = rawOptions ? JSON.parse(rawOptions) : []

    if (!Array.isArray(parsedOptions)) {
      return []
    }

    return parsedOptions
      .filter((option): option is BackendServerOption =>
        Boolean(option?.isActive && option.name && option.url),
      )
      .map((option, index) => ({
        id: `stored-${index}-${option.url}`,
        name: option.name,
        url: normalizeUrl(option.url),
      }))
  } catch {
    return []
  }
}

export const getServerOptions = (): ServerOption[] => {
  const options = [...parseConfiguredServerOptions()]
  const fallbackUrl = AppConfig.getBaseURL()

  if (fallbackUrl) {
    options.unshift({
      id: `default-${fallbackUrl}`,
      name: 'Default',
      url: normalizeUrl(fallbackUrl),
    })
  }

  options.push(...getStoredServerOptions())

  return options.filter(
    (option, index, allOptions) =>
      allOptions.findIndex((currentOption) => currentOption.url === option.url) === index,
  )
}
